import React, { useRef } from "react";
import { useFrame, useThree, Canvas } from "@react-three/fiber"; // Added Canvas, useThree
import { Outlines, Environment, useGLTF } from "@react-three/drei"; // Changed useTexture to useGLTF
import { Physics, useBox, useSphere } from "@react-three/cannon"; // Changed useSphere to useBox
import { EffectComposer, N8AO, SMAA, Bloom } from "@react-three/postprocessing";
// import { useControls } from "leva";
import * as THREE from "three"; // Ensure THREE is imported

// IMPORTANT: Replace '/path/to/your/model.glb' with the actual path to your GLB file.
// For example, if your Dino model is in public/models/dino.glb, use '/dino.glb'.
// You might need to inspect your GLB file (e.g., in a viewer like https://gltf.report/)
// to find the correct mesh name within the 'nodes' object.
const GLB_MODEL_PATH = "/heart.glb"; // Placeholder: Update this to your actual GLB path

const rfs = THREE.MathUtils.randFloatSpread;
// baubleMaterial can be used, or you can use the GLB's inherent materials.
// For simplicity, we'll keep baubleMaterial for now, but note the comment below.
const baubleMaterial = new THREE.MeshStandardMaterial({
  color: "white",
  roughness: 0,
  envMapIntensity: 0.5,
});

export const Dice = () => (
  <div className="w-screen h-screen">
    <Canvas
      shadows
      gl={{ antialias: false }}
      dpr={[1, 1]} 
      camera={{ position: [0, 0, 20], fov: 35, near: 1, far: 40 }}
    >
      <ambientLight intensity={0.5} />
      <color attach="background" args={["#dfdfdf"]} />
      <spotLight
        intensity={1}
        color="blue"
        angle={0.2}
        penumbra={1}
        position={[30, 30, 30]}
        castShadow
        shadow-mapSize={[512, 512]}
      />
      <Physics gravity={[0, 2, 0]} iterations={10}>
        <Pointer />
        <Clump />
      </Physics>
      <Environment preset="dawn" />
      <EffectComposer disableNormalPass multisampling={0}>
        {/* N8AO: Reduce samples, intensity, or radius */}
      {/* <N8AO halfRes color="black" aoRadius={1.5} intensity={0.8} aoSamples={4} denoiseSamples={2} />  */}
      {/* Adjusted */}
      {/* Bloom: Reduce levels or intensity */}
      <Bloom mipmapBlur levels={5} intensity={0.5} /> 
      {/* Adjusted */}
      {/* SMAA: Keep if needed, it's generally lighter than other AA methods */}
      <SMAA />
      </EffectComposer>
    </Canvas>
  </div>
);

function Clump({
  mat = new THREE.Matrix4(),
  vec = new THREE.Vector3(),
  ...props
}) {
  // const { outlines } = useControls({
  //   outlines: { value: 0.0, step: 0.01, min: 0, max: 0.05 },
  // });

  // Load your GLB model
  const { nodes } = useGLTF(GLB_MODEL_PATH);

  // Identify the geometry from your loaded GLB model.
  // console.log("GLB Nodes:", nodes); // Uncomment to inspect your GLB's structure in console

  // This attempts to find a suitable mesh geometry.
  // Replace 'DinoModel' with the actual name of the mesh in your GLB if known.
  // If your GLB has multiple meshes, you might need to pick the correct one.
  const glbGeometry = nodes.DinoModel ? nodes.DinoModel.geometry : null;

  // Fallback: Find the first mesh geometry if 'DinoModel' isn't found
  const primaryMeshKey = Object.keys(nodes).find((key) => nodes[key].isMesh);
  const effectiveGeometry =
    glbGeometry || (primaryMeshKey ? nodes[primaryMeshKey].geometry : null);

  // IMPORTANT: If no geometry is found, you might want to render a warning or fallback.
  if (!effectiveGeometry) {
    console.warn(
      `No suitable geometry found in GLB at ${GLB_MODEL_PATH}. Check 'nodes' structure.`
    );
    return null;
  }

  // Determine the collider arguments for useBox based on your GLB model's size.
  // For a precise fit, you should compute the bounding box of your GLB model's geometry:
  effectiveGeometry.computeBoundingBox();
  const size = effectiveGeometry.boundingBox.getSize(new THREE.Vector3());
  // useBox args are [width, height, depth] for @react-three/cannon
  const colliderArgs = [size.x, size.y, size.z];

  // Use useBox for the physics collider from @react-three/cannon
  const [ref, api] = useBox(() => ({
    args: colliderArgs, // Use the dynamically computed collider arguments
    mass: 1,
    angularDamping: 0.1,
    linearDamping: 0.65,
    position: [rfs(20), rfs(20), rfs(20)], // Initial random positions
  }));

  useFrame((state) => {
    // Only apply forces if the ref and API are ready
    if (!ref.current || !api) return;

    for (let i = 0; i < 40; i++) {
      // Get current whereabouts of the instanced mesh
      ref.current.getMatrixAt(i, mat);
      // Normalize the position and multiply by a negative force to pull towards center
      api.at(i).applyForce(
        vec
          .setFromMatrixPosition(mat)
          .normalize()
          .multiplyScalar(-40) // Adjust force strength as needed
          .toArray(),
        [0, 0, 0] // Apply force at the center of the instance
      );
    }
  });

  return (
    <instancedMesh
      ref={ref}
      castShadow
      receiveShadow
      scale={1.5}
      // Use the GLB's geometry and the defined baubleMaterial.
      // If your GLB has its own materials you want to use, you'd need a more complex setup here
      // (e.g., iterating through nodes.materials or using a custom shader).
      args={[effectiveGeometry, baubleMaterial, 40]}
    >
      <Outlines thickness={2}  />
    </instancedMesh>
  );
}

function Pointer() {
  const viewport = useThree((state) => state.viewport);
  // useSphere from @react-three/cannon
  const [ref, api] = useSphere(() => ({
    type: "Kinematic",
    args: [3],
    position: [0, 0, 0],
  }));
  useFrame((state) =>
    api.position.set(
      (state.mouse.x * viewport.width) / 2,
      (state.mouse.y * viewport.height) / 2,
      0
    )
  );
  return (
    <mesh ref={ref} scale={0.2}>
      <sphereGeometry />
      <meshBasicMaterial color={[4, 4, 4]} toneMapped={false} />
      <pointLight intensity={8} distance={10} />
    </mesh>
  );
}
